import { useMemo, useState } from 'react';
import type { ColumnDef, ColorIntent, HeatmapMapping, VisualizationConfig } from '@deckspace/types';
import { getThemeColors } from '../../theme/index.js';
import { prepHeatmapSeries } from './utils.js';

interface Props {
  rows: Record<string, unknown>[];
  columnDefs: ColumnDef[];
  mapping: HeatmapMapping;
  config: VisualizationConfig;
  width?: number;
  height?: number;
}

// Parse color string (hex or rgb) to RGB tuple
function parseColor(c: string): [number, number, number] {
  if (c.startsWith('#')) {
    const hex = c.slice(1);
    const fullHex =
      hex.length === 3
        ? hex
            .split('')
            .map((ch) => ch + ch)
            .join('')
        : hex;
    const num = parseInt(fullHex, 16);
    return [(num >> 16) & 0xff, (num >> 8) & 0xff, num & 0xff];
  }
  const match = c.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (match && match[1] && match[2] && match[3]) {
    return [parseInt(match[1], 10), parseInt(match[2], 10), parseInt(match[3], 10)];
  }
  return [128, 128, 128];
}

function interpolateMultiStop(colors: string[], factor: number): string {
  if (colors.length === 0) return 'rgb(128,128,128)';
  if (colors.length === 1) return colors[0] ?? 'rgb(128,128,128)';

  const f = Math.max(0, Math.min(1, factor));
  const segments = colors.length - 1;
  const scaledFactor = f * segments;
  const segmentIndex = Math.min(Math.floor(scaledFactor), segments - 1);
  const segmentFactor = scaledFactor - segmentIndex;

  const color1 = parseColor(colors[segmentIndex] ?? '');
  const color2 = parseColor(colors[segmentIndex + 1] ?? '');

  const r = Math.round(color1[0] + segmentFactor * (color2[0] - color1[0]));
  const g = Math.round(color1[1] + segmentFactor * (color2[1] - color1[1]));
  const b = Math.round(color1[2] + segmentFactor * (color2[2] - color1[2]));

  return `rgb(${r},${g},${b})`;
}

const HEATMAP_PALETTES: Record<ColorIntent | 'default', string[]> = {
  neutral: [
    '#313695',
    '#4575b4',
    '#74add1',
    '#abd9e9',
    '#e0f3f8',
    '#ffffbf',
    '#fee090',
    '#fdae61',
    '#f46d43',
    '#d73027',
    '#a50026',
  ],
  diverging: [
    '#2166ac',
    '#4393c3',
    '#92c5de',
    '#d1e5f0',
    '#f7f7f7',
    '#fddbc7',
    '#f4a582',
    '#d6604d',
    '#b2182b',
  ],
  'positive-negative': [
    '#1a9850',
    '#66bd63',
    '#a6d96a',
    '#d9ef8b',
    '#f7f7f7',
    '#fee08b',
    '#fdae61',
    '#f46d43',
    '#d73027',
  ],
  comparison: [
    '#313695',
    '#4575b4',
    '#74add1',
    '#abd9e9',
    '#e0f3f8',
    '#ffffbf',
    '#fee090',
    '#fdae61',
    '#f46d43',
    '#d73027',
    '#a50026',
  ],
  default: [
    '#313695',
    '#4575b4',
    '#74add1',
    '#abd9e9',
    '#e0f3f8',
    '#ffffbf',
    '#fee090',
    '#fdae61',
    '#f46d43',
    '#d73027',
    '#a50026',
  ],
};

export function Heatmap({ rows, columnDefs, mapping, config }: Props) {
  const [hoveredCell, setHoveredCell] = useState<{ x: number; y: number; value: number } | null>(
    null
  );

  const themeColors = getThemeColors();
  const palette = useMemo(() => {
    const intent = config.intent || 'neutral';
    return HEATMAP_PALETTES[intent] || HEATMAP_PALETTES.default;
  }, [config.intent]);

  const series = useMemo(
    () =>
      rows.length === 0
        ? { xLabels: [], yLabels: [], matrix: [] }
        : prepHeatmapSeries(rows, columnDefs, mapping),
    [rows, columnDefs, mapping]
  );

  const colorScale = useMemo(() => {
    if (!series.matrix.length) {
      return (_v: number) => '#f0f0f0';
    }
    const flat = series.matrix.flat();
    const min = Math.min(...flat);
    const max = Math.max(...flat);

    return (v: number) => {
      const factor = max === min ? 0.5 : (v - min) / (max - min);
      return interpolateMultiStop(palette, factor);
    };
  }, [series.matrix, palette]);

  if (!series.xLabels.length || !series.yLabels.length || !series.matrix.length) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        No data available
      </div>
    );
  }

  const xLabels = series.xLabels;
  const yLabels = series.yLabels;
  const matrix = series.matrix;

  const cols = xLabels.length;
  const rowCount = yLabels.length;
  const cellWidth = 100 / cols;
  const cellHeight = 100 / rowCount;

  const formatValue = (value: number) => {
    return value.toLocaleString();
  };

  return (
    <div className="relative w-full h-full min-h-[200px]">
      <svg viewBox="-20 -10 130 120" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
        {/* Y-axis labels */}
        {yLabels.map((label: string, y: number) => (
          <text
            key={`y-${y}`}
            x={-2}
            y={y * cellHeight + cellHeight / 2}
            textAnchor="end"
            dominantBaseline="middle"
            fontSize={3}
            fill={themeColors.textSecondary}
          >
            {label.length > 10 ? label.slice(0, 10) + '...' : label}
          </text>
        ))}

        {/* X-axis labels */}
        {xLabels.map((label: string, x: number) => (
          <text
            key={`x-${x}`}
            x={x * cellWidth + cellWidth / 2}
            y={104}
            textAnchor="middle"
            fontSize={3}
            fill={themeColors.textSecondary}
          >
            {label.length > 8 ? label.slice(0, 8) + '...' : label}
          </text>
        ))}

        {/* Cells */}
        {matrix.map((row: number[], y: number) =>
          row.map((value: number, x: number) => (
            <rect
              key={`${x}-${y}`}
              x={x * cellWidth}
              y={y * cellHeight}
              width={cellWidth}
              height={cellHeight}
              fill={colorScale(value)}
              stroke={themeColors.border}
              strokeWidth={0.3}
              onMouseEnter={() => setHoveredCell({ x, y, value })}
              onMouseLeave={() => setHoveredCell(null)}
              style={{ cursor: 'pointer' }}
            />
          ))
        )}
      </svg>

      {/* Tooltip */}
      {hoveredCell && (
        <div className="absolute top-2 right-2 bg-card border border-border rounded-md px-3 py-2 shadow-lg text-sm pointer-events-none">
          <div className="text-muted-foreground text-xs">
            {xLabels[hoveredCell.x]} / {yLabels[hoveredCell.y]}
          </div>
          <div className="text-foreground font-medium">{formatValue(hoveredCell.value)}</div>
        </div>
      )}
    </div>
  );
}
